import { useEffect, useState } from "react";
import { FieldAttributes, useField, useFormikContext } from "formik";
import AsyncSelectLib from "react-select/async";
import { components } from "react-select";
import { SelectOption } from "types/SelectTypes";
import SVG from "shared/SVG/SVG";
import { SVG_ENUM } from "enums";
import "./Select.scss";

interface AsyncSelectProps extends FieldAttributes<any> {
  label?: string;
  name: string;
  placeholder?: string;
  className?: string;
  optional?: boolean;
  disabled?: boolean;
  loadOptions: (inputValue: string) => Promise<SelectOption[]>;
  onSelectOption?: (opt: any | null) => void;
  allowFreeInput?: boolean;
  isClearable?: boolean;
  onInputTyping?: (value: string) => void;
  minCharsToSearch?: number;
}

const AsyncSelectComponent = ({
  label,
  name,
  placeholder,
  className = "",
  optional = false,
  disabled = false,
  loadOptions,
  onSelectOption,
  allowFreeInput = true,
  isClearable = true,
  onInputTyping,
  minCharsToSearch = 2,
  ...props
}: AsyncSelectProps) => {
  const [field, meta] = useField({ ...props, name });
  const formik = useFormikContext();

  const [inputValue, setInputValue] = useState<string>(field.value || "");
  const [selectedOption, setSelectedOption] = useState<SelectOption | null>(
    null
  );
  const [hasResults, setHasResults] = useState<boolean>(false);

  useEffect(() => {
    if (selectedOption && selectedOption.value !== field.value) {
      setSelectedOption(null);
      setInputValue(field.value || "");
    } else if (!selectedOption) {
      setInputValue(field.value || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field.value]);

  const handleChange = (opt: any | null) => {
    const valueToStore = opt ? opt.value : "";
    setSelectedOption(opt ? opt : null);
    setInputValue(opt ? String(opt.label) : "");
    formik.setFieldValue(field.name, valueToStore);
    if (onSelectOption) onSelectOption(opt);
  };

  const fetchOptionsManually = async (raw: string) => {
    const q = (raw || "").trim();
    if (q.length < minCharsToSearch) return;
    try {
      const res = await loadOptions(q);
      setHasResults((res || []).length > 0);
    } catch {
      setHasResults(false);
    }
  };

  const wrappedLoadOptions = async (inputValue: string) => {
    const q = (inputValue || "").trim();
    if (q.length < minCharsToSearch) {
      setHasResults(false);
      return [];
    }
    const res = await loadOptions(q);
    setHasResults((res || []).length > 0);
    return res;
  };

  const {
    onChange: _ignoredOnChange,
    onBlur: _ignoredOnBlur,
    ...restProps
  } = props as any;

  const CustomDropdownIndicator = (props: any) => {
    const { selectProps } = props;
    return (
      <components.DropdownIndicator {...props}>
        <SVG
          type={
            selectProps.menuIsOpen ? SVG_ENUM.CHEVRON_UP : SVG_ENUM.CHEVRON_DOWN
          }
        />
      </components.DropdownIndicator>
    );
  };

  return (
    <div className="select__wrapper async">
      {label ? (
        <label className="select__label" htmlFor={props.id || name}>
          {label}{" "}
          {optional && <span className="input__optional">(opcjonalne)</span>}
        </label>
      ) : null}
      <AsyncSelectLib
        cacheOptions
        defaultOptions={false}
        loadOptions={wrappedLoadOptions}
        value={selectedOption}
        onChange={(opt) => handleChange(opt as any)}
        inputValue={inputValue}
        onInputChange={(val, meta) => {
          if (meta.action === "input-change") {
            if (allowFreeInput) {
              formik.setFieldValue(field.name, val);
            }
            setSelectedOption(null);
            setInputValue(val);
            onInputTyping && onInputTyping(val);

            const trimmed = (val || "").trim();
            if (trimmed.length < minCharsToSearch) {
              setHasResults(false);
            } else {
              fetchOptionsManually(val);
            }
          }
          return val;
        }}
        onBlur={() => formik.setFieldTouched(field.name, true, true)}
        placeholder={placeholder || ""}
        className={`select ${className}`}
        classNamePrefix="select"
        isDisabled={disabled}
        isClearable={isClearable}
        noOptionsMessage={() =>
          (inputValue || "").trim().length < minCharsToSearch
            ? `Wpisz min. ${minCharsToSearch} znaki`
            : ""
        }
        loadingMessage={() => null}
        isLoading={false}
        inputId={props.id || name}
        openMenuOnClick={false}
        openMenuOnFocus={false}
        menuIsOpen={
          (inputValue || "").trim().length >= minCharsToSearch && hasResults
            ? undefined
            : false
        }
        components={{
          DropdownIndicator: CustomDropdownIndicator,
          LoadingIndicator: () => null,
        }}
        {...restProps}
      />
      {meta.touched && meta.error && (
        <div className="input__error input__error--select">
          <span>{meta.error}</span>
        </div>
      )}
    </div>
  );
};

export default AsyncSelectComponent;
